<template>
  <section class="ticket-flow-timeline">
    <header class="ticket-flow-section-heading">
      <strong>Workflow timeline</strong>
      <span>{{ periods.length }} {{ periods.length === 1 ? 'status' : 'statuses' }}</span>
    </header>

    <div v-if="loading && !periods.length" class="ticket-flow-timeline__skeleton" aria-label="Loading workflow history">
      <span v-for="index in 3" :key="index"></span>
    </div>
    <div v-else-if="error && !periods.length" class="ticket-flow-timeline__state" role="alert">
      <p>Unable to load workflow history.</p>
      <button type="button" @click="load(true)">Retry</button>
    </div>
    <div v-else-if="periods.length" class="ticket-flow-timeline__list">
      <article v-for="period in periods" :key="period.id" class="ticket-flow-timeline__period">
        <div class="ticket-flow-timeline__rail"><span :style="{ backgroundColor: statusColor(period.status) }"></span></div>
        <div class="ticket-flow-timeline__content">
          <div class="ticket-flow-timeline__period-heading">
            <app-status-badge class="ticket-flow-timeline__stage" design="new" :color="statusColor(period.status)" :label="statusLabel(period.status)" />
            <time :title="formatDateTime(period.entered_at)">{{ relativeTime(period.entered_at) }}</time>
          </div>
          <p v-if="period.current" class="ticket-flow-timeline__current">Current status</p>
          <p class="ticket-flow-timeline__meta">
            {{ period.actor ? period.actor.full_name : 'System' }} · {{ durationLabel(period.duration_seconds) }}
          </p>
          <p v-if="period.waiting_for" class="ticket-flow-timeline__reason">Waiting for {{ waitingLabel(period.waiting_for) }}</p>
          <p v-if="period.resolution" class="ticket-flow-timeline__reason">{{ resolutionLabel(period.resolution) }}</p>
          <time v-if="period.exited_at" class="ticket-flow-timeline__range">{{ formatDateTime(period.entered_at) }} — {{ formatDateTime(period.exited_at) }}</time>
        </div>
      </article>
      <button v-if="hasMore" type="button" class="ticket-flow-timeline__more" :disabled="loadingMore" @click="load(false)">
        {{ loadingMore ? 'Loading…' : 'Show older activity' }}
      </button>
    </div>
    <p v-else class="ticket-flow-timeline__empty">No workflow history yet</p>
  </section>
</template>

<script>
import { getSupportTicketTimeline } from '@/http'
import { formatDateTime, relativeTime, resolutionLabel, statusLabel, statusTone, waitingLabel } from '../ticketState'

const STATUS_COLORS = { new: '#22a06b', in_progress: '#2563eb', waiting: '#d97706', closed: '#64748b' }

export default {
  name: 'SupportTicketTimeline',
  props: {
    ticketId: { type: Number, required: true },
    refreshKey: { type: Number, default: 0 }
  },
  data() {
    return { periods: [], cursor: null, hasMore: false, loading: false, loadingMore: false, error: false, generation: 0 }
  },
  watch: {
    ticketId: { immediate: true, handler() { this.reset(); this.load(true) } },
    refreshKey() { this.load(true) }
  },
  methods: {
    formatDateTime,
    relativeTime,
    resolutionLabel,
    statusLabel,
    statusTone,
    waitingLabel,
    statusColor(status) { return STATUS_COLORS[status] || '#879392' },
    durationLabel(seconds = 0) {
      if (seconds < 60) return '< 1 minute'
      if (seconds < 3600) return `${Math.floor(seconds / 60)} min`
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} h`
      return `${Math.floor(seconds / 86400)} d`
    },
    reset() { this.generation += 1; this.periods = []; this.cursor = null; this.hasMore = false; this.loading = false; this.loadingMore = false; this.error = false },
    load(reset) {
      if (reset ? this.loading : (!this.hasMore || this.loadingMore)) return Promise.resolve()
      const generation = reset ? this.generation + 1 : this.generation
      this.generation = generation
      if (reset) { this.loading = true; this.periods = []; this.cursor = null } else this.loadingMore = true
      this.error = false
      return getSupportTicketTimeline(this.ticketId, { page_size: 10, cursor: reset ? undefined : this.cursor || undefined }).then(response => {
        if (generation !== this.generation) return
        const incoming = response.data.results || []
        const seen = new Set((reset ? [] : this.periods).map(item => item.id))
        this.periods = (reset ? [] : this.periods).concat(incoming.filter(item => !seen.has(item.id)))
        this.cursor = response.data.next_cursor || null
        this.hasMore = Boolean(response.data.has_more && this.cursor)
      }).catch(() => { if (generation === this.generation) this.error = true })
        .finally(() => { if (generation === this.generation) { this.loading = false; this.loadingMore = false } })
    }
  }
}
</script>

<style lang="scss" scoped>
.ticket-flow-timeline { min-width: 0; padding: 20px; border: 1px solid $ui-line; border-radius: 16px; background: $ui-surface; box-shadow: 0 8px 26px rgba(28, 45, 74, .055); }
.ticket-flow-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.ticket-flow-section-heading strong { color: $ui-ink; font-family: $ui-font-heading; font-size: 16px; font-weight: 600; letter-spacing: 0; text-transform: none; }
.ticket-flow-section-heading span { color: $ui-muted; font-size: 14px; }
.ticket-flow-timeline__list { margin-top: 15px; }
.ticket-flow-timeline__period { display: grid; grid-template-columns: 18px minmax(0, 1fr); }
.ticket-flow-timeline__rail { position: relative; display: flex; justify-content: center; }
.ticket-flow-timeline__rail::after { position: absolute; top: 14px; bottom: -15px; width: 1px; background: #dfe4dd; content: ''; }
.ticket-flow-timeline__period:last-of-type .ticket-flow-timeline__rail::after { display: none; }
.ticket-flow-timeline__rail span { z-index: 1; width: 9px; height: 9px; margin-top: 8px; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 1px #d7ddd5; }
.ticket-flow-timeline__content { min-width: 0; padding: 0 0 22px 8px; }
.ticket-flow-timeline__period-heading { display: flex; min-width: 0; align-items: center; justify-content: space-between; gap: 9px; }
.ticket-flow-timeline__stage { overflow: hidden; padding: 4px 9px; border: 1px solid; border-radius: 8px; font-size: 12px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.ticket-flow-timeline__period-heading time, .ticket-flow-timeline__range { color: $ui-muted; font-size: 12px; white-space: nowrap; }
.ticket-flow-timeline__current { margin: 5px 0 0; color: $ui-blue; font-size: 13px; font-weight: 600; }
.ticket-flow-timeline__meta, .ticket-flow-timeline__reason { margin: 5px 0 0; color: $ui-text; font-size: 14px; line-height: 1.45; }
.ticket-flow-timeline__reason { color: #74817f; }
.ticket-flow-timeline__range { display: block; margin-top: 5px; overflow: hidden; text-overflow: ellipsis; }
.ticket-flow-timeline__more { width: 100%; min-height: 32px; border: 1px solid #d7e2cb; border-radius: 5px; background: #f8fbf5; color: #5c8d20; cursor: pointer; font-size: 10px; font-weight: 700; }
.ticket-flow-timeline__more:disabled { cursor: wait; opacity: .6; }
.ticket-flow-timeline__state button { padding: 0; border: 0; background: transparent; color: #55940a; cursor: pointer; font-size: 10px; font-weight: 700; }
.ticket-flow-timeline__empty, .ticket-flow-timeline__state p { margin: 15px 0 0; color: #8a9695; font-size: 11px; }
.ticket-flow-timeline__skeleton { display: grid; gap: 12px; margin-top: 15px; }
.ticket-flow-timeline__skeleton span { display: block; height: 54px; border-radius: 5px; background: #eef1ec; }
</style>
